import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import opportunityService from '../../services/opportunityService';
import './AdminOpportunityManager.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://safepumback.civicstech.name.ng/api';

const AdminOpportunityManager = () => {
  const { user } = useAuth();
  const [opportunities, setOpportunities] = useState([]);

  // Utility function to safely format numbers
  const formatNumber = (value, decimals = 1) => {
    if (value === null || value === undefined || value === '') return '0';
    const num = parseFloat(value);
    return isNaN(num) ? '0' : num.toFixed(decimals);
  };

  // Utility function to safely parse numbers for comparison
  const parseNumber = (value) => {
    if (value === null || value === undefined || value === '') return 0;
    const num = parseFloat(value);
    return isNaN(num) ? 0 : num;
  };
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [formData, setFormData] = useState({
    token_address: '',
    opportunity_type: 'volume_spike',
    confidence_score: 0.5,
    trigger_price: '',
    volume_change_24h: '',
    price_change_1h: '',
    description: '',
    expires_at: ''
  });

  const isAdmin = () => {
    return user && (user.role === 'admin' || user.isAdmin || user.isPoolManager);
  };

  useEffect(() => {
    if (isAdmin()) {
      fetchOpportunities();
    } else {
      setLoading(false);
    }
  }, [user]);

  const fetchOpportunities = async () => {
    try {
      setLoading(true);
      console.log('🔍 Testing API connection...');

      // First test basic connectivity without auth
      const testResponse = await fetch(`${API_BASE_URL}/opportunities/active`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      console.log('📡 API Response Status:', testResponse.status);
      console.log('📡 API Response Headers:', Object.fromEntries(testResponse.headers.entries()));

      if (!testResponse.ok) {
        const errorText = await testResponse.text();
        console.error('❌ API Error Response:', errorText);
        throw new Error(`HTTP ${testResponse.status}: ${errorText}`);
      }

      const data = await testResponse.json();
      console.log('✅ API Response Data:', data);

      if (data.success) {
        setOpportunities(data.data.opportunities);
        setError(null);
      } else {
        setError('Failed to fetch opportunities: ' + (data.error || 'Unknown error'));
      }
    } catch (err) {
      console.error('🚨 API Error details:', err);
      if (err.message.includes('Failed to fetch') || err.message.includes('Network error')) {
        setError(`Network error: Cannot connect to the server at ${API_BASE_URL}. Please check if the backend is running.`);
      } else if (err.message.includes('CORS')) {
        setError('CORS error: Cross-origin request blocked. Please check server configuration.');
      } else {
        setError(`Failed to fetch opportunities: ${err.message || 'Unknown error'}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);

      // Set default expiration if not provided
      const submitData = {
        ...formData,
        expires_at: formData.expires_at || new Date(Date.now() + 2 * 60 * 60 * 1000).toISOString()
      };

      const response = await opportunityService.createOpportunity(submitData);

      if (response.success) {
        setShowCreateForm(false);
        setFormData({
          token_address: '',
          opportunity_type: 'volume_spike',
          confidence_score: 0.5,
          trigger_price: '',
          volume_change_24h: '',
          price_change_1h: '',
          description: '',
          expires_at: ''
        });
        fetchOpportunities(); // Refresh the list
        alert('Trading opportunity created successfully!');
      } else {
        alert('Failed to create opportunity: ' + (response.error || 'Unknown error'));
      }
    } catch (err) {
      if (err.message.includes('401') || err.message.includes('Unauthorized')) {
        alert('Authentication failed. Please log in again as an administrator.');
      } else if (err.message.includes('403') || err.message.includes('Forbidden')) {
        alert('Access denied. You need administrator privileges to create trading opportunities.');
      } else {
        alert('Error creating opportunity: ' + err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (opportunityId) => {
    if (!window.confirm('Are you sure you want to delete this trading opportunity?')) {
      return;
    }

    try {
      setLoading(true);
      const response = await opportunityService.deleteOpportunity(opportunityId);

      if (response.success) {
        fetchOpportunities(); // Refresh the list
        alert('Trading opportunity deleted successfully!');
      } else {
        alert('Failed to delete opportunity: ' + (response.error || 'Unknown error'));
      }
    } catch (err) {
      if (err.message.includes('401') || err.message.includes('Unauthorized')) {
        alert('Authentication failed. Please log in again as an administrator.');
      } else if (err.message.includes('403') || err.message.includes('Forbidden')) {
        alert('Access denied. You need administrator privileges to delete trading opportunities.');
      } else {
        alert('Error deleting opportunity: ' + err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const getOpportunityIcon = (type) => {
    switch (type) {
      case 'volume_spike': return '🔥';
      case 'price_breakout': return '📈';
      case 'trend_reversal': return '🔄';
      case 'social_buzz': return '📢';
      default: return '⭐';
    }
  };

  const getOpportunityLabel = (type) => {
    switch (type) {
      case 'volume_spike': return 'Volume Spike';
      case 'price_breakout': return 'Price Breakout';
      case 'trend_reversal': return 'Trend Reversal';
      case 'social_buzz': return 'Social Buzz';
      default: return 'Unknown';
    }
  };

  // Check admin access after all hooks
  if (!isAdmin()) {
    return (
      <div className="admin-opportunity-manager">
        <div className="admin-access-denied">
          <h2>Access Denied</h2>
          <p>You don't have permission to manage trading opportunities.</p>
          <p>Please contact an administrator if you believe this is an error.</p>
        </div>
      </div>
    );
  }

  if (loading && opportunities.length === 0) {
    return (
      <div className="admin-opportunity-manager">
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <span>Loading trading opportunities...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-opportunity-manager">
      <div className="admin-header">
        <h2>Trading Opportunities Management</h2>
        <button
          className="create-btn"
          onClick={() => setShowCreateForm(!showCreateForm)}
        >
          {showCreateForm ? 'Cancel' : '+ Create Opportunity'}
        </button>
      </div>

      {error && (
        <div className="error-message">
          <span>⚠️ {error}</span>
          <button
            className="retry-btn"
            onClick={fetchOpportunities}
            style={{ marginLeft: '10px', padding: '4px 8px', fontSize: '12px' }}
          >
            🔄 Retry
          </button>
        </div>
      )}

      {showCreateForm && (
        <div className="create-form-container">
          <form onSubmit={handleSubmit} className="create-form">
            <h3>Create New Trading Opportunity</h3>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="token_address">Token Address *</label>
                <input
                  type="text"
                  id="token_address"
                  name="token_address"
                  value={formData.token_address}
                  onChange={handleInputChange}
                  placeholder="e.g., So11111111111111111111111111111111111111112"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="opportunity_type">Opportunity Type *</label>
                <select
                  id="opportunity_type"
                  name="opportunity_type"
                  value={formData.opportunity_type}
                  onChange={handleInputChange}
                  required
                >
                  <option value="volume_spike">Volume Spike</option>
                  <option value="price_breakout">Price Breakout</option>
                  <option value="trend_reversal">Trend Reversal</option>
                  <option value="social_buzz">Social Buzz</option>
                </select>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="confidence_score">Confidence Score (0-1) *</label>
                <input
                  type="number"
                  id="confidence_score"
                  name="confidence_score"
                  value={formData.confidence_score}
                  onChange={handleInputChange}
                  min="0"
                  max="1"
                  step="0.01"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="trigger_price">Trigger Price</label>
                <input
                  type="number"
                  id="trigger_price"
                  name="trigger_price"
                  value={formData.trigger_price}
                  onChange={handleInputChange}
                  step="0.000001"
                  placeholder="Optional"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="volume_change_24h">Volume Change 24h (%)</label>
                <input
                  type="number"
                  id="volume_change_24h"
                  name="volume_change_24h"
                  value={formData.volume_change_24h}
                  onChange={handleInputChange}
                  step="0.01"
                  placeholder="e.g., 250.00"
                />
              </div>

              <div className="form-group">
                <label htmlFor="price_change_1h">Price Change 1h (%)</label>
                <input
                  type="number"
                  id="price_change_1h"
                  name="price_change_1h"
                  value={formData.price_change_1h}
                  onChange={handleInputChange}
                  step="0.01"
                  placeholder="e.g., 12.50"
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="description">Description</label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                placeholder="Describe the trading opportunity..."
                rows="3"
              />
            </div>

            <div className="form-group">
              <label htmlFor="expires_at">Expiration Date/Time</label>
              <input
                type="datetime-local"
                id="expires_at"
                name="expires_at"
                value={formData.expires_at}
                onChange={handleInputChange}
                placeholder="Leave empty for 2 hours from now"
              />
            </div>

            <div className="form-actions">
              <button type="submit" className="submit-btn" disabled={loading}>
                {loading ? 'Creating...' : 'Create Opportunity'}
              </button>
              <button
                type="button"
                className="cancel-btn"
                onClick={() => setShowCreateForm(false)}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="opportunities-list">
        <h3>Active Trading Opportunities ({opportunities.length})</h3>

        {opportunities.length === 0 ? (
          <div className="no-opportunities">
            <span className="no-opportunities-icon">🔍</span>
            <span>No active trading opportunities</span>
            <small>Create your first opportunity above</small>
          </div>
        ) : (
          <div className="opportunities-grid">
            {opportunities.map((opportunity) => (
              <div key={opportunity.id} className="opportunity-card">
                <div className="opportunity-header">
                  <div className="opportunity-type">
                    <span className="type-icon">{getOpportunityIcon(opportunity.opportunity_type)}</span>
                    <span className="type-label">{getOpportunityLabel(opportunity.opportunity_type)}</span>
                  </div>
                  <div className="opportunity-actions">
                    <button
                      className="delete-btn"
                      onClick={() => handleDelete(opportunity.id)}
                      title="Delete opportunity"
                    >
                      🗑️
                    </button>
                  </div>
                </div>

                <div className="opportunity-details">
                  <div className="detail-row">
                    <span className="label">Token:</span>
                    <span className="value token-address">
                      {opportunity.token_address.slice(0, 8)}...{opportunity.token_address.slice(-8)}
                    </span>
                  </div>

                  <div className="detail-row">
                    <span className="label">Confidence:</span>
                    <span className="value confidence">
                      {(parseNumber(opportunity.confidence_score) * 100).toFixed(1)}%
                    </span>
                  </div>

                  {opportunity.trigger_price && parseNumber(opportunity.trigger_price) > 0 && (
                    <div className="detail-row">
                      <span className="label">Trigger Price:</span>
                      <span className="value">${formatNumber(opportunity.trigger_price, 6)}</span>
                    </div>
                  )}

                  {opportunity.volume_change_24h && parseNumber(opportunity.volume_change_24h) !== 0 && (
                    <div className="detail-row">
                      <span className="label">Volume Change:</span>
                      <span className={`value ${parseNumber(opportunity.volume_change_24h) > 0 ? 'positive' : 'negative'}`}>
                        {parseNumber(opportunity.volume_change_24h) > 0 ? '+' : ''}{formatNumber(opportunity.volume_change_24h)}%
                      </span>
                    </div>
                  )}

                  {opportunity.price_change_1h && parseNumber(opportunity.price_change_1h) !== 0 && (
                    <div className="detail-row">
                      <span className="label">Price Change (1h):</span>
                      <span className={`value ${parseNumber(opportunity.price_change_1h) > 0 ? 'positive' : 'negative'}`}>
                        {parseNumber(opportunity.price_change_1h) > 0 ? '+' : ''}{formatNumber(opportunity.price_change_1h)}%
                      </span>
                    </div>
                  )}

                  <div className="detail-row">
                    <span className="label">Expires:</span>
                    <span className="value">
                      {new Date(opportunity.expires_at).toLocaleString()}
                    </span>
                  </div>

                  {opportunity.description && (
                    <div className="detail-row description">
                      <span className="label">Description:</span>
                      <span className="value">{opportunity.description}</span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminOpportunityManager;